import { useEffect, useState } from "react";
import { PassageNotFoundError, retreivePassageText, UnauthorizedError } from "./services/passageService";
import Footer from "./components/Footer";
import Header from "./components/Header";
import MessageModal from "./components/MessageModal";
import { CustomText, isCustomText, Passage, InputRow } from "./utils/inputRow";
import { OutputElement } from "./utils/outputElement";
import LoginForm from "./components/LoginForm";
import PassageTable from "./components/table/PassageTable";
import SlideOptions, { DEFAULT_SLIDE_OPTIONS_DATA } from "./components/SlideOptions";
import SlideResults from "./components/result/SlideResults";
import { MessageType } from "./contexts/MessageContext";
import { useShowMessage } from "./hooks/useShowMessage";

const App = () => {
  const [token, setToken] = useState<string | null>(null);
  const [outputElements, setOutputElements] = useState<OutputElement[]>([]);
  const [slideOptionsData, setSlideOptionsData] = useState(DEFAULT_SLIDE_OPTIONS_DATA);
  const [fadeIn, setFadeIn] = useState(false); // fade-in state
  const showMessage = useShowMessage();

  useEffect(() => {
    if (!token) {
      setFadeIn(false);
      return;
    }
    setFadeIn(false);
    const id = requestAnimationFrame(() => setFadeIn(true));

    const savedRows = localStorage.getItem("rowInputs");
    if (!savedRows) return;
    const rowInputs: InputRow[] = JSON.parse(savedRows);
    updateSlideResults(rowInputs);

    return () => cancelAnimationFrame(id);
  }, [token]);

  const updateSlideResults = async (rowInputs: InputRow[]) => {
    const results: (CustomText | { passage: Passage; text: string })[] = [];
    for (let i = 0; i < rowInputs.length; i++) {
      const row = rowInputs[i];
      try {
        const result = isCustomText(row) ? row : await retreivePassageText(row as Passage);
        const mapped = typeof result === "string" ? { passage: row as Passage, text: result } : (result as CustomText);
        results.push(mapped);
      } catch (err) {
        if (err instanceof UnauthorizedError) {
          await showMessage(err.message, MessageType.Error);
          localStorage.removeItem("token");
          window.location.reload();
        } else if (err instanceof PassageNotFoundError) {
          const pErr = err as PassageNotFoundError;
          const msg = `The passage ${pErr.book} ${pErr.chapter},${pErr.verses} (${pErr.translation}) was not found!\nPlease check your last entry.`;
          await showMessage(msg, MessageType.Error);
        } else {
          await showMessage((err as Error).message, MessageType.Error);
        }
        return;
      }
    }
    setOutputElements(results);
  };

  const retrieveTexts = (newRowInputs: InputRow[]) => {
    localStorage.setItem("rowInputs", JSON.stringify(newRowInputs));
    updateSlideResults(newRowInputs);
  };

  return (
    <div>
      <div className="relative min-h-screen">
        <div className="pb-20">
          <Header />
          <div className={`${token ? "hidden" : ""}`}>
            <LoginForm onLoginSuccess={setToken} />
          </div>
          <div
            className={`container m-auto transition-opacity duration-700 ease-in-out will-change-[opacity] transform-gpu
    ${token ? (fadeIn ? "opacity-100" : "opacity-0") : "opacity-0 pointer-events-none"}
  `}
          >
            {token && (
              <div className="mb-12 md:mb-0">
                <PassageTable onUpdate={retrieveTexts} />
                <SlideOptions onUpdate={(e) => setSlideOptionsData(e)} />
                {outputElements.length > 0 && (
                  <SlideResults outputElements={outputElements} slideOptionsData={slideOptionsData} />
                )}
              </div>
            )}
          </div>
        </div>
        <Footer />
      </div>
      <MessageModal />
    </div>
  );
};

export default App;
